import { Buffer } from 'buffer';
import { Connection } from '@solana/web3.js';
import nacl from 'tweetnacl';
import bs58 from 'bs58';
// OA-0001 assessment: this file used to carry a presigned-transaction validator
// (`validatePresignedSignedTransaction` plus its borsh parser and the
// ATA/sponsor setup guards) for the retired client-side transaction-building
// lane (`runTransaction`, deleted from AuthProvider when EVM removal took its
// last live caller). Nothing called it any more, and it carried the same
// comparison gap as the finding's client lane (program/app/path checks only,
// operation contents skipped), so it was REMOVED rather than left as a dead
// validator a future caller could revive unbound. The live server-side signing
// lane - WalletClient.setMany -> @bounded-sh/core operations.setMany ->
// handlePreBuiltTransaction - now binds the full requested operation intent
// before signing (see tarobase-core src/client/solana-operations.ts).
/* ──────────────────────────────────────────────────────────
 * Helper – fetch getPriorityFeeEstimate
 * ──────────────────────────────────────────────────────── */
async function fetchPriorityFee(rpcEndpoint, rawUnsignedTxBuffer) {
    var _a, _b;
    try {
        const res = await fetch(rpcEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                jsonrpc: "2.0",
                id: "1",
                method: "getPriorityFeeEstimate",
                params: [{
                        transaction: bs58.encode(rawUnsignedTxBuffer), options: {
                            recommended: true
                        }
                    }]
            })
        });
        const data = await res.json();
        const fee = (_b = (_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.priorityFeeEstimate) !== null && _b !== void 0 ? _b : null;
        if (fee != null && fee > 0) {
            return Math.ceil(Number(fee) * 1.2);
        }
        console.warn("Priority-fee estimate returned no positive fee; skipping priority fee instruction");
        return null;
    }
    catch (err) {
        console.warn("Priority-fee estimate failed; skipping priority fee instruction:", err);
        return null;
    }
}
/**
 * DS3-0414: does the transaction already carry a REAL signer signature? A Solana
 * signature is computed over the compiled message, which INCLUDES recentBlockhash, so
 * the blockhash must not be rewritten once any signer (sponsor / multisig / co-signer)
 * has signed - a rewrite silently invalidates their signature. Legacy signatures are
 * `{ signature: Buffer | null }` entries; versioned signatures are 64-byte arrays that
 * stay all-zero placeholders until a signer fills them. Mirrors turnkey-signer-bridge.ts.
 */
function transactionHasSignature(transaction) {
    if ('serializeMessage' in transaction) {
        return transaction.signatures.some((s) => s.signature != null);
    }
    return transaction.signatures.some((sig) => sig.some((b) => b !== 0));
}
/** The highest Solana transaction version this provider reads back (SIMD-0385 v1). */
const SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION = 1;
/**
 * getTransaction that survives a SIMD-0385 v1 transaction. @solana/web3.js 1.98.x
 * validates the response `version` against `0 | 'legacy'` and throws on 1 even when
 * the request raised the ceiling. Legacy and v0 keep returning web3.js's parsed
 * object; when web3.js refuses to parse and the connection exposes its endpoint, the
 * same result is read as raw JSON-RPC (plain `transaction.message` object, no class
 * helpers).
 */
async function getTransactionCompat(connection, signature) {
    var _a;
    try {
        return await connection.getTransaction(signature, {
            maxSupportedTransactionVersion: SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION,
            commitment: 'confirmed',
        });
    }
    catch (error) {
        const endpoint = typeof (connection === null || connection === void 0 ? void 0 : connection.rpcEndpoint) === 'string' ? connection.rpcEndpoint.trim() : '';
        if (!endpoint)
            throw error;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'getTransaction',
                params: [signature, {
                        commitment: 'confirmed',
                        encoding: 'json',
                        maxSupportedTransactionVersion: SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION,
                    }],
            }),
        });
        if (!response.ok)
            throw error;
        const body = await response.json();
        if (body === null || body === void 0 ? void 0 : body.error)
            throw error;
        return (_a = body === null || body === void 0 ? void 0 : body.result) !== null && _a !== void 0 ? _a : null;
    }
}
export class SolanaKeypairProvider {
    constructor(rpcUrl, serverKeypair) {
        this.rpcUrl = rpcUrl;
        this.serverKeypair = serverKeypair;
    }
    get keypair() {
        if (!this.serverKeypair) {
            throw new Error('Server keypair is required; use createWalletClient({ keypair }) or pass a Keypair to SolanaKeypairProvider.');
        }
        return this.serverKeypair;
    }
    /* ----------------------------------------------------------- *
     * (Auth stubs – fill in later if needed)
     * ----------------------------------------------------------- */
    async login() { throw new Error('Not implemented'); }
    async restoreSession() { throw new Error('Not implemented'); }
    async logout() { throw new Error('Not implemented'); }
    /* ----------------------------------------------------------- *
     * Sign arbitrary message
     * ----------------------------------------------------------- */
    async signMessage(message) {
        const sig = nacl.sign.detached(new TextEncoder().encode(message), this.keypair.secretKey);
        return Buffer.from(sig).toString('base64');
    }
    /* ----------------------------------------------------------- *
     * Sign transaction
     * ----------------------------------------------------------- */
    async signTransaction(transaction) {
        // Use duck typing instead of instanceof to handle multiple @solana/web3.js versions
        const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
        if (isLegacyTransaction) {
            transaction.partialSign(this.keypair);
        }
        else {
            transaction.sign([this.keypair]);
        }
        return transaction;
    }
    /**
     * SIMD-0385 v1: the server keypair signs the exact message bytes; kit places the
     * signature in this key's slot and leaves every other slot (sponsor,
     * attestation) untouched. Refuses when the keypair is not a required signer,
     * because a stray signature would be silently dropped and the write would fail
     * later with a misleading missing-signature error.
     */
    async signTransactionV1(transaction) {
        const kit = await import('@solana/kit');
        const signerAddress = this.keypair.publicKey.toBase58();
        if (!Object.prototype.hasOwnProperty.call(transaction.signatures, signerAddress)) {
            throw new Error(`The configured server keypair (${signerAddress}) is not a required signer of this v1 transaction.`);
        }
        const keyPair = await kit.createKeyPairFromBytes(this.keypair.secretKey);
        return await kit.partiallySignTransaction([keyPair], transaction);
    }
    /** A server keypair can sign every format; the write request advertises all three. */
    supportedTransactionVersions() {
        return ['legacy', 0, 1];
    }
    /**
     * Signs and submits a Solana transaction to the network.
     *
     * This method handles blockhash and transaction confirmation automatically - you do NOT need to
     * set recentBlockhash or lastValidBlockHeight on the transaction before calling this method.
     * Requires an explicit RPC URL configured on the provider because this method has no
     * Solana transaction payload network to resolve.
     *
     * @param transaction - The transaction to sign and submit (Transaction or VersionedTransaction)
     * @param feePayer - Optional fee payer public key. If not provided and the transaction doesn't
     *                   already have a feePayer set, the keypair's public key will be used.
     *                   Useful for co-signing scenarios where a different account pays the fees.
     * @returns The transaction signature
     */
    async signAndSubmitTransaction(transaction, feePayer) {
        // 1. Get RPC URL and create connection
        const rpcUrl = this.getRpcUrl();
        const connection = new Connection(rpcUrl, 'confirmed');
        // 2. Get fresh blockhash and set it on the transaction before signing.
        // DS3-0414: a Solana signature covers the compiled message (recentBlockhash included),
        // so refreshing the blockhash after a sponsor / co-signer has already signed silently
        // invalidates their signature - and this is the sponsored/fee-payer submit path, where a
        // co-signed transaction is exactly what arrives. signTransaction only ADDS this keypair's
        // signature (partialSign), it never re-signs the others. So only refresh the blockhash
        // while the transaction is still unsigned; once a real signature is present, sign and
        // submit the caller's message verbatim.
        if (!transactionHasSignature(transaction)) {
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
            // Use duck typing instead of instanceof to handle multiple @solana/web3.js versions
            const isLegacyTransaction = 'recentBlockhash' in transaction && !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacyTransaction) {
                const legacyTx = transaction;
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
                // Set feePayer if not already set
                if (!legacyTx.feePayer) {
                    legacyTx.feePayer = feePayer !== null && feePayer !== void 0 ? feePayer : this.keypair.publicKey;
                }
            }
            else {
                // VersionedTransaction
                const versionedTx = transaction;
                versionedTx.message.recentBlockhash = blockhash;
                // Note: VersionedTransaction feePayer is set in the message at creation time
                // and cannot be modified after creation
            }
        }
        // 3. Sign the transaction
        const signedTx = await this.signTransaction(transaction);
        // 4. Submit and confirm using shared helper
        const { signature } = await this.submitAndConfirmTransaction(signedTx, connection);
        return signature;
    }
    /* ----------------------------------------------------------- *
     * Private Helpers
     * ----------------------------------------------------------- */
    /**
     * Submits a signed transaction and waits for confirmation using polling.
     * Shared by both signAndSubmitTransaction and runTransactionInner.
     */
    async submitAndConfirmTransaction(signedTx, connection, options) {
        var _a, _b, _c, _d, _e;
        const sig = await connection.sendRawTransaction(signedTx.serialize(), {
            preflightCommitment: 'confirmed'
        });
        // Wait for confirmation using polling
        const startTime = Date.now();
        const timeoutMs = 10 * 1000; // 10 seconds
        while (true) {
            const st = await connection.getSignatureStatus(sig);
            if ((_a = st === null || st === void 0 ? void 0 : st.value) === null || _a === void 0 ? void 0 : _a.err) {
                // Transaction confirmed but failed on-chain — fetch logs for a readable error
                let logMessages;
                try {
                    logMessages = (_c = (_b = (await getTransactionCompat(connection, sig))) === null || _b === void 0 ? void 0 : _b.meta) === null || _c === void 0 ? void 0 : _c.logMessages;
                }
                catch (_f) {
                    // Log retrieval is diagnostic only; the on-chain error is the verdict.
                }
                const errorMessage = logMessages ? JSON.stringify(logMessages) : JSON.stringify((_d = st === null || st === void 0 ? void 0 : st.value) === null || _d === void 0 ? void 0 : _d.err);
                throw new Error(`Transaction failed: ${errorMessage}`);
            }
            if (((_e = st === null || st === void 0 ? void 0 : st.value) === null || _e === void 0 ? void 0 : _e.confirmationStatus) === 'confirmed') {
                break;
            }
            // Check if we've exceeded the timeout
            if (Date.now() - startTime > timeoutMs) {
                throw new Error(`Transaction confirmation timeout after ${timeoutMs / 1000} seconds`);
            }
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        // Optionally fetch transaction info
        let txInfo = null;
        if (options === null || options === void 0 ? void 0 : options.fetchTxInfo) {
            txInfo = await getTransactionCompat(connection, sig);
        }
        return { signature: sig, txInfo };
    }
    getRpcUrl() {
        var _a;
        const explicitRpcUrl = (_a = this.rpcUrl) === null || _a === void 0 ? void 0 : _a.trim();
        if (explicitRpcUrl) {
            return explicitRpcUrl;
        }
        throw new Error('Solana RPC URL is required for server keypair transaction submission; pass an explicit rpcUrl when creating SolanaKeypairProvider.');
    }
    async getNativeMethods() {
        return { keypair: this.keypair };
    }
}
//# sourceMappingURL=solana-keypair-provider.js.map